import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import APIHandler from 'config/APIHandler';

import { useCooldown } from 'hooks/useCooldown';
import { useSettings } from 'hooks/useSettings';
import { useUserInfo } from 'hooks/useUserInfo';

const pointsHandlerContext = createContext(null);

const defaultPointsHandlerValues = {
  loaded: false,
  totalPoints: 0,
  tiers: [],
  completedItems: [],
  latestUpdate: null,
  latestLeveled: null,
  lastFetched: null,
};

const defaultStartingTier = {
  points: 0,
  title: 'None',
  copy: 'Congratulations, you have unlocked the first level!',
  image: null,
};

const PointsHandlerProvider = ({ children }) => {
  const {
    appId,
    loaded: loadedSettings,
    lang: { langFile },
  } = useSettings();
  const {
    user: { email },
    loaded: loadedUser,
  } = useUserInfo();
  const [pointsHandlerInfo, setPointsHandlerInfo] = useState(defaultPointsHandlerValues);

  const fetchPointsHandlerInfo = useCallback(async () => {
    if (!loadedSettings || !loadedUser) return 'Settings or user not loaded';
    const { result, response } = (await APIHandler.getUserPointsNew(
      appId,
      email,
      `${langFile}-${langFile.replace('es', '41').replace('ko', 'kr').replace('pt', 'br').toUpperCase()}`,
    )) || {
      result: 'error',
      response: 'No response from API',
    };

    if (result === 'error') return response;

    const { tiers, totalPoints, completedItems } = response;

    // Includes the previous state in the new state to prevent overwriting the most recent update and leveled values (if any)
    setPointsHandlerInfo((prev) => ({
      ...prev,
      loaded: true,
      tiers,
      totalPoints,
      completedItems,
      lastFetched: new Date().getTime(),
    }));

    return true;
  }, [appId, email, loadedSettings, loadedUser, langFile]);

  const refreshData = useCallback(() => {
    const { loaded } = pointsHandlerInfo;
    if (loaded) {
      fetchPointsHandlerInfo();
    }
  }, [fetchPointsHandlerInfo, pointsHandlerInfo]);

  // Wraps the refresh function in the cooldown hook to prevent spamming the API
  const refreshWithCooldown = useCooldown(refreshData, 15 * 1000);

  const setPoints = useCallback(
    async (updates) => {
      const { loaded: loadedPointsHandlerInfo, completedItems } = pointsHandlerInfo;

      // If the points handler info is not loaded, escape and return an error.
      if (!loadedSettings || !loadedUser || !loadedPointsHandlerInfo)
        return { result: 'error', response: 'not loaded' };

      // Ensure updates is an array
      const updatesArray = Array.isArray(updates) ? updates : [updates];

      const usedActionValues = completedItems.map((item) => {
        const { action: completedAction, action_value: completedValue } = item;
        return { action: completedAction, value: completedValue };
      });

      const unusedActions = updatesArray.filter(
        (newAction) =>
          !usedActionValues.some(
            (usedAction) => usedAction.action === newAction.action && usedAction.value === newAction.value,
          ),
      );

      // Break if no updates are left after filtering for used action/values
      if (unusedActions.length === 0) return { result: 'error', response: 'already used' };

      const updateResponse = await APIHandler.setUserPointsNew(appId, email, unusedActions);
      const { result, response } = updateResponse;

      if (result === 'success') {
        const { tiers, totalPoints, completedItems: completedItemsResponse, update, leveled } = response;

        const updatedPointsHandlerInfo = {
          loaded: true,
          tiers,
          totalPoints,
          completedItems: completedItemsResponse,
          latestUpdate: update,
          latestLeveled: leveled,
        };
        setPointsHandlerInfo(updatedPointsHandlerInfo);
        fetchPointsHandlerInfo(); // Fetch the latest points handler info to update the completedItems
      }
      return updateResponse;
    },
    [appId, email, loadedSettings, loadedUser, pointsHandlerInfo, fetchPointsHandlerInfo],
  );

  /**
   * Returns either the index of the current tier or an element from a provided array matching that tier index, based on
   * the user's points.
   *
   * @param {Array} [array=null] - An optional array from which to return a value corresponding to the tier index.
   *   Default is `null`
   * @returns {number | any | null} - Returns the index of the tier or an element from the provided array corresponding
   *   to the tier index. If `pointsHandler` is not defined, returns null.
   */
  const getTieredValue = useCallback(
    (array = null) => {
      const { loaded, tiers, totalPoints } = pointsHandlerInfo;
      if (!loaded) return null;

      let index = 0;
      tiers.forEach(({ points: tierPoints }, i) => {
        // Could be improved, maybe using filter / reduce?
        if (totalPoints >= tierPoints) {
          index = i;
        }
      });

      if (array) return array[index < 0 ? 0 : index];
      return index;
    },
    [pointsHandlerInfo],
  );

  const pointsHandlerValues = useMemo(() => {
    const { loaded, tiers, totalPoints, completedItems, latestUpdate, latestLeveled } = pointsHandlerInfo;

    return {
      loaded,
      tiers,
      startingTier: defaultStartingTier,
      totalPoints,
      completedItems,
      latestUpdate,
      latestLeveled,
      refreshData: refreshWithCooldown,
      fetchPointsHandlerInfo,
      setPoints,
      getTieredValue,
    };
  }, [pointsHandlerInfo, refreshWithCooldown, fetchPointsHandlerInfo, setPoints, getTieredValue]);

  return <pointsHandlerContext.Provider value={pointsHandlerValues}>{children}</pointsHandlerContext.Provider>;
};

/**
 * @typedef {object} Leveled - A 'level up' object from a points update
 * @property {boolean} leveled - Whether the user has leveled up.
 * @property {number} points - The points gained from the level up.
 * @property {string} title - The level up title (tier).
 * @property {string} copy - The level up congratulatory message.
 * @property {string} image - The level up image.
 */

/**
 * @typedef {object} Update - An update object
 * @property {number} points - The points gained from the update.
 * @property {string} copy - The update message.
 * @property {string} type - The update type.
 * @property {string} image - The update image.
 */

/**
 * @typedef {object} Tier - A points tier object
 * @property {number} points - The tier's points threshold (tier is reached when user's points are greater than or equal
 *   to this value)
 * @property {string} title - The tier's title
 * @property {string} copy - The tier's congratulatory message for reaching this tier
 * @property {string} image - The tier's image name
 */

/**
 * @typedef {object} CompletedItem - A completed item (action) object entry in the backend
 * @property {number} id - The item ID
 * @property {string} app_id - The application ID. This maps to the appId obtained in useSettings()
 * @property {string} sub_app_id - The sub application ID
 * @property {number} user_id - The user's ID
 * @property {number} xp - The xp (points) gained from completing this item.
 * @property {string} action - The type of action that this item represents. e.g. 'intro-chapter', 'specialization', or 'subapp'.
 * @property {string} action_value - The value of the action at the moment of update. e.g. 'intro-chapter-${id}',
 *   'specialization-${id}', or 'subapp-${appSessionId}'.
 * @property {string} created_at - The created date. This is a string in the format 'YYYY-MM-DD HH:mm:ss'.
 * @property {string} updated_at - The updated date. This is a string in the format 'YYYY-MM-DD HH:mm:ss'.
 * @property {string} deleted_at - The deleted date. This is a string in the format 'YYYY-MM-DD HH:mm:ss'.
 * @property {string} badge - The associated badge image (if any)
 * @property {string} region - The region
 * @property {string} tier - The associated tier. Usually one of: ['bronze', 'silver', 'gold', 'platinum']
 */

/**
 * @typedef {object} PointsHandlerData - Hook return data
 * @property {boolean} loaded - Whether the points handler is loaded.
 * @property {CompletedItem[]} completedItems - A list of completed items (actions) that have been stored in the backend.
 * @property {Leveled} latestLeveled - The latest level-up notification object from a points update (if any).
 * @property {Update} latestUpdate - The update object from a points update (if any).
 * @property {Tier[]} tiers - The different reacheable tiers.
 * @property {Tier} startingTier - A hardcoded starting tier (0 points).
 * @property {number} totalPoints - The total points.
 * @property {Function} setPoints - The set points function. Updates the points handler and backend data
 * @property {Function} getTieredValue - The get tiered value function.
 */

/**
 * A wrapper hook for managing the pointsHandler class/object. This hook manages the pointsHandler object, its
 * initialization etc. so that it does not need to be managed in the component.
 *
 * @returns {PointsHandlerData} - Returns the points handler data.
 */
const usePointsHandler = () => {
  const context = useContext(pointsHandlerContext);
  if (!context) {
    throw new Error('usePointsHandler must be used within a PointsHandlerProvider');
  }
  const {
    loaded,
    tiers,
    startingTier,
    totalPoints,
    completedItems,
    latestUpdate,
    latestLeveled,
    fetchPointsHandlerInfo,
    setPoints,
    getTieredValue,
    refreshData,
  } = context;

  useEffect(() => {
    const fetchPointsHandler = async () => {
      if (!loaded) {
        await fetchPointsHandlerInfo();
      }
    };
    fetchPointsHandler();
  }, [fetchPointsHandlerInfo, loaded]);

  useEffect(() => {
    if (loaded) {
      refreshData();
    }
  }, [refreshData, loaded]);

  return {
    loaded,
    completedItems,
    latestLeveled,
    latestUpdate,
    tiers,
    startingTier,
    totalPoints,
    setPoints,
    getTieredValue,
  };
};

PointsHandlerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { PointsHandlerProvider, usePointsHandler };
